import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteVerificationDocument,
  downloadVerificationDocument,
  listVerificationDocuments,
  uploadVerificationDocuments,
  VerificationDocumentType
} from '../api/kyc';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../auth/AuthContext';

const TYPES: Array<{ value: VerificationDocumentType; label: string }> = [
  { value: 'id_card', label: 'Carte d’identité' },
  { value: 'passport', label: 'Passeport' },
  { value: 'driver_license', label: 'Permis' },
  { value: 'business_registration', label: 'Registre entreprise' },
  { value: 'insurance', label: 'Assurance' },
  { value: 'vehicle_registration', label: 'Carte grise' },
  { value: 'other', label: 'Autre' }
];

export const KycPage = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [documentType, setDocumentType] = useState<VerificationDocumentType>('id_card');
  const [files, setFiles] = useState<File[]>([]);

  const userId = user?.id ?? '';

  const docsQuery = useQuery({
    queryKey: ['kyc', userId],
    queryFn: () => listVerificationDocuments(userId),
    enabled: Boolean(userId)
  });

  const uploadMutation = useMutation({
    mutationFn: () => uploadVerificationDocuments({ userId, documentType, files }),
    onSuccess: async () => {
      setFiles([]);
      await qc.invalidateQueries({ queryKey: ['kyc', userId] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => deleteVerificationDocument(userId, docId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['kyc', userId] });
    }
  });

  const canUpload = useMemo(() => Boolean(userId) && files.length > 0, [files.length, userId]);

  return (
    <div className="space-y-6">
      <PageHeader title="KYC" description="Déposer et gérer vos documents de vérification." />

      <div className="card p-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Type de document</span>
            <select className="input" value={documentType} onChange={(e) => setDocumentType(e.target.value as VerificationDocumentType)}>
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1 md:col-span-2">
            <span className="text-sm font-medium text-slate-700">Fichiers (max 5)</span>
            <input
              className="input"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 5))}
            />
          </label>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className="btn-primary"
            disabled={!canUpload || uploadMutation.isPending}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? 'Upload…' : 'Envoyer'}
          </button>
          {uploadMutation.isError && (
            <div className="text-sm text-red-600">Erreur: {(uploadMutation.error as Error).message}</div>
          )}
          {uploadMutation.isSuccess && <div className="text-sm text-emerald-700">Documents envoyés.</div>}
        </div>

      </div>

      <div className="card p-0 overflow-hidden">
        <div className="border-b border-slate-200/80 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Mes documents</h2>
          <p className="text-sm text-slate-600">Téléchargement/suppression selon les permissions serveur.</p>
        </div>
        <div className="divide-y divide-slate-200/80">
          {docsQuery.isLoading && <div className="p-5 text-sm text-slate-500">Chargement…</div>}
          {docsQuery.isError && <div className="p-5 text-sm text-red-600">Erreur: {(docsQuery.error as Error).message}</div>}
          {docsQuery.isSuccess && docsQuery.data.data.length === 0 && (
            <div className="p-5 text-sm text-slate-500">Aucun document.</div>
          )}
          {docsQuery.isSuccess &&
            docsQuery.data.data.map((d) => (
              <div key={d.id} className="p-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{d.originalName}</div>
                  <div className="text-xs text-slate-500">
                    {d.documentType} · {Math.round(d.size / 1024)} Ko · {new Date(d.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-ghost !py-2"
                    onClick={async () => {
                      const { blob, filename } = await downloadVerificationDocument(userId, d.id);
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Télécharger
                  </button>
                  <button
                    type="button"
                    className="btn-ghost !py-2 text-red-600 hover:bg-red-50"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(d.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

